import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Upload, FileSignature, Loader2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeaseUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${API}/leases/upload/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Ce lien est invalide ou a expiré."));
  }, [token]);

  const submit = async () => {
    if (!file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/leases/upload/${token}`, fd);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Échec de l'envoi, réessayez.");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="HSH Ops" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <div className="font-bold font-display text-slate-900">HSH Ops</div>
            <div className="text-xs text-slate-500">Dépôt de bail signé</div>
          </div>
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="upload-error">{error}</div>}

        {!error && !info && <div className="text-sm text-slate-500">Chargement…</div>}

        {info && !done && (
          <>
            <div className="bg-slate-50 rounded-xl p-4 mb-4 text-sm">
              <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                <FileSignature className="w-4 h-4 text-blue-600" />{info.apartment?.name}
              </div>
              <div className="text-slate-500 mt-1">{info.apartment?.address}</div>
              <div className="text-slate-500 mt-0.5">Période : {info.start_date} → {info.end_date}</div>
              <div className="text-slate-500 mt-0.5">Locataire : {info.tenant_name}</div>
            </div>
            {info.status === "signe_recu" ? (
              <div className="text-center py-6" data-testid="bm-already-uploaded">
                <Clock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <div className="font-semibold text-slate-900">Document déjà déposé</div>
                <p className="text-sm text-slate-500 mt-1">
                  Votre bail signé est en cours de vérification. Si un nouveau document est nécessaire,
                  vous recevrez un email avec les corrections à apporter.
                </p>
              </div>
            ) : (
            <div className="mt-4 space-y-3">
              <button onClick={() => window.open(`${API}/leases/public/${token}/contrat`, "_blank")}
                      data-testid="bm-download-contract"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 transition-colors">
                <FileSignature className="w-4 h-4" />Télécharger le contrat à signer
              </button>
              <input ref={ref} type="file" accept=".pdf,image/*" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bm-file-input" />
              <button onClick={() => ref.current?.click()} data-testid="bm-choose-file"
                className="w-full border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors">
                <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1.5" />
                <div className="text-sm text-slate-600 font-medium">
                  {file ? file.name : "Choisir le bail signé (PDF ou photo)"}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Depuis votre appareil ou votre appareil photo</div>
              </button>
              <button onClick={submit} disabled={!file || sending} data-testid="bm-submit"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 transition-colors">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Envoyer le bail signé
              </button>
            </div>
            )}
          </>
        )}

        {done && (
          <div className="text-center py-6" data-testid="bm-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <div className="font-bold font-display text-slate-900 text-lg">Bail reçu, merci !</div>
            <p className="text-sm text-slate-500 mt-1">
              Votre bail signé a été transmis. Vous recevrez une confirmation après vérification.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
